import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';

import { useState, useCallback, useEffect } from 'react';

import { useDispatch } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { setErrorMessage } from 'common/store/reducer/commonReducer';
import { urlWithTenant } from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import { getControlDeDupTableData } from '../api';
import { IControlDeDupTableData } from '../api/type-def';

import styles from './ControlDeDuplicationLandingPage.module.scss';

const { aimodels, controlDeDuplication, deDuplicationClusterReview } =
	rrcpDashboardRoutes;

const { OK, BAD_REQUEST, NOT_FOUND } = HTTP_STATUS_CODES;

const ControlDeDuplicationTable = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { tableQueryObj, updateTableQuery } = useTableQuery({
		id: 'ControlDeDuplicationTable',
	});

	const [, setError] = useState<boolean>(false);
	const [isLoading, setLoading] = useState<boolean>(false);
	const [isError404, setError404] = useState<boolean>(false);
	const [isToastOpen, setIsToastOpen] = useState(false);
	const [toastMessage, setToastMessageLocal] = useState('');
	const [controlDeDupTableData, setControlDeDupTableData] = useState<
		IControlDeDupTableData[]
	>([]);
	const [totalRecordsCount, setTotalRecordCount] = useState<number>(0);
	const [totalPagesCount, setTotalPagesCount] = useState(0);
	const [pageNumber, setPageNumber] = useState(0);

	const handleToast = useCallback(() => {
		setIsToastOpen(false);
		setToastMessageLocal('');
	}, []);

	const navigateToReviewPage = (clusterName: string, clusterSize: number) => {
		if (clusterSize > 0) {
			navigate(
				urlWithTenant(
					`/${aimodels}/${controlDeDuplication}/${deDuplicationClusterReview}`,
				),
				{
					state: {
						clusterName,
					},
				},
			);
		}
	};

	const columns: readonly Column[] = [
		{
			id: 'clusterId',
			label: 'Clusters',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Typography
					component="div"
					data-testid="name-link"
					onClick={() =>
						navigateToReviewPage(params?.clusterId, params?.clusterSize)
					}
					className={`${
						params?.clusterSize > 0 && styles['table-row-clickable']
					}`}
				>
					{params?.clusterId}
				</Typography>
			),
		},
		{
			id: 'clusterSize',
			label: 'Cluster Size',
			disablePadding: false,
			isSortable: true,
		},
		{ id: 'assessmentUnit', label: 'Assessment Unit', disablePadding: false },
		{ id: 'reviewStatus', label: 'Review Status', disablePadding: false },
		{ id: 'workflowStatus', label: 'Workflow Status', disablePadding: false },
	];

	const getControlDeDupTableResponseData = async (data: any) => {
		setError(false);
		setLoading(true);
		const response = await getControlDeDupTableData(data);
		if (response?.statusCode === OK) {
			if (response?.responseBody) {
				const responseData = response?.responseBody;
				const tempTableData = response?.responseBody?.clusterDetails;
				const updatedData = tempTableData.map((item: any) => ({
					...item,
				}));
				setControlDeDupTableData(updatedData);
				setTotalRecordCount(responseData?.totalCount);
				setTotalPagesCount(responseData?.totalPages);
				setPageNumber(responseData?.pageNumber);
				setLoading(false);
			}
		} else if (response?.statusCode === BAD_REQUEST) {
			setLoading(false);
			dispatch(setErrorMessage('Bad request, Please try again later.'));
		} else if (response?.statusCode === NOT_FOUND) {
			setError404(true);
			setLoading(false);
		} else {
			setError(true);
			setToastMessageLocal(APP_GENERAL_ERROR_MSG);
			setLoading(false);
		}
	};

	useEffect(() => {
		getControlDeDupTableResponseData(tableQueryObj);
	}, []);

	const handlePageChange = (data: any, flag?: boolean) => {
		if (flag) {
			updateTableQuery(data);
			getControlDeDupTableResponseData(data);
		}
	};

	const handleSortChange = (data: any) => {
		getControlDeDupTableResponseData(data);
	};

	return (
		<Box>
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<Box
					className={styles['regulations-table-box']}
					data-testid="regulation-table-box"
				>
					<TableComponent
						defaultQuery={tableQueryObj}
						rows={controlDeDupTableData}
						columns={columns}
						totalItems={totalRecordsCount}
						defaultSortKey=""
						onPageHandler={handlePageChange}
						isLoading={isLoading}
						tableStyles={{ minWidth: '46.875em', maxHeight: '27.5em' }}
						totalPagesCount={totalPagesCount}
						onSortHandler={handleSortChange}
						customErrorMessage={
							controlDeDupTableData.length === 0 && pageNumber === 0
								? 'Please Run cluster for the first time to display the results'
								: 'No Data found'
						}
						isError={isError404}
						enableHeaderColor
						isRenderPagination
					/>
					<AlertComponent
						open={isToastOpen}
						toastMessage={toastMessage}
						severity="error"
						handleCloseMessage={handleToast}
						vertical="top"
						horizontal="right"
					/>
				</Box>
			</Grid>
		</Box>
	);
};

export default ControlDeDuplicationTable;
